/* ==========================================================================
   Session Expired Modal
   Task F26: Displays a modal when the user's session has expired,
   prompting them to log in again.
   ========================================================================== */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { Alert } from '@/components/ui/Alert'
import { LogIn, AlertCircle } from 'lucide-react'
import { useAuthContext } from '@/context/AuthContext'
import { usePortalSlug } from '@/context/PortalContext'
import { sessionExpiredEvent, SESSION_EXPIRED_EVENT } from '@/utils/sessionEvents'

/* --------------------------------------------------------------------------
   Session Expired Modal Component
   -------------------------------------------------------------------------- */

/**
 * Modal that displays when a session expires.
 * Allows users to re-login without losing their current page context.
 *
 * This component should be placed near the root of the app and will
 * automatically show when a SESSION_EXPIRED_EVENT is dispatched.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <AuthProvider>
 *       <SessionExpiredModal />
 *       <Routes>...</Routes>
 *     </AuthProvider>
 *   )
 * }
 * ```
 */
export function SessionExpiredModal() {
  const [isOpen, setIsOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { login, logout, user } = useAuthContext()
  const slug = usePortalSlug()
  const navigate = useNavigate()
  const location = useLocation()

  // Pre-fill email if we have the user's info
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email)
    }
  }, [user?.email])

  // Listen for session expired events
  useEffect(() => {
    const handleSessionExpired = () => {
      setIsOpen(true)
      setError(null)
      setPassword('')
    }

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired)

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired)
    }
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)
      setLoading(true)

      try {
        const outcome = await login(email, password, true)
        if (outcome === 'signed_in') {
          setIsOpen(false)
          setPassword('')
          // Stay on current page - the user's work is preserved
        } else if (outcome === 'two_factor_required') {
          // PORTAL-187: the password was right but the account needs its
          // authenticator code. The code step lives on the login page (it keeps the
          // pending challenge from the context), so drop the expired session and go
          // there, coming back here afterwards. Clearing the in-memory user stops
          // Login bouncing straight back.
          setIsOpen(false)
          setPassword('')
          const redirect = encodeURIComponent(location.pathname + location.search)
          await logout()
          navigate(`/rx/${slug}/login?redirect=${redirect}`)
        } else {
          setError('Invalid email or password. Please try again.')
        }
      } catch {
        setError('An error occurred. Please try again.')
      } finally {
        setLoading(false)
      }
    },
    [email, password, login, logout, navigate, slug, location.pathname, location.search]
  )

  const handleGoToLogin = useCallback(() => {
    setIsOpen(false)
    // Navigate to login page with redirect back to current page
    const loginPath = slug ? `/rx/${slug}/login` : '/login'
    navigate(loginPath, {
      state: { from: location.pathname },
    })
  }, [slug, navigate, location.pathname])

  // Don't render if modal shouldn't be open
  if (!isOpen) return null

  return (
    <Modal
      open={isOpen}
      onClose={() => {}} // Prevent closing by clicking outside
      title="Session Expired"
      size="sm"
      showCloseButton={false}
    >
      <div className="space-y-4">
        {/* Icon */}
        <div className="mx-auto w-12 h-12 bg-[var(--color-warning-light)] rounded-full flex items-center justify-center">
          <AlertCircle
            className="w-6 h-6 text-[var(--color-warning)]"
            aria-hidden="true"
          />
        </div>

        {/* Message */}
        <p className="text-center text-[var(--color-charcoal-light)]">
          Your session has expired. Please log in again to continue.
        </p>

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            {error}
          </Alert>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField label="Email" htmlFor="session-email">
            <Input
              id="session-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={loading}
            />
          </FormField>
          <FormField label="Password" htmlFor="session-password">
            <Input
              id="session-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={loading}
            />
          </FormField>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2">
            <Button
              type="submit"
              variant="primary"
              loading={loading}
              className="w-full"
            >
              <LogIn className="w-4 h-4 mr-2" aria-hidden="true" />
              Log In
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleGoToLogin}
              disabled={loading}
              className="w-full"
            >
              Go to Login Page
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

/**
 * Utility function to trigger the session expired modal.
 * Can be called from anywhere in the app (e.g., API interceptors).
 */
export function triggerSessionExpired(): void {
  sessionExpiredEvent.dispatch()
}

export default SessionExpiredModal
